(function () {
  'use strict';

  const config = window.rccProductCardConfig || {};

  function shopifyId(value) {
    return String(value || '').split('@')[0].split('/').pop();
  }

  function productUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      url.search = '';
      url.hash = '';
      return url.href;
    } catch (_error) {
      return '';
    }
  }

  if (!customElements.get('rcc-product-wishlist')) {
    const wishlist = {
      swat: null,
      variants: new Map(),
      products: new Set(),
      components: new Set(),
      initialized: new WeakSet(),
      refreshVersion: 0,
    };

    const syncWishlist = () => {
      // Keep the product heart filled while any of its variants remains saved.
      wishlist.products = new Set(wishlist.variants.values());
      wishlist.components.forEach((component) => component.updateState());
    };

    const refreshWishlist = () => {
      const { swat } = wishlist;
      if (typeof swat?.fetchLists !== 'function') return;
      wishlist.refreshVersion += 1;
      const { refreshVersion } = wishlist;

      try {
        swat.fetchLists({
          callbackFn: (lists) => {
            if (wishlist.swat !== swat || refreshVersion !== wishlist.refreshVersion) return;
            const items = (Array.isArray(lists) ? lists : []).flatMap(
              (list) => list.listcontents || [],
            );
            wishlist.variants = new Map(
              items.map((item) => [shopifyId(item.epi), shopifyId(item.empi)])
                .filter(([variantId, productId]) => variantId && productId),
            );
            syncWishlist();
          },
        });
      } catch (_error) {
        // Swym can still process a click when its initial fetch is unavailable.
      }
    };

    const updateFromEvent = (event, added) => {
      const variantId = shopifyId(event.detail?.d?.epi);
      const productId = shopifyId(event.detail?.d?.empi);
      if (added && variantId && productId) {
        wishlist.variants.set(variantId, productId);
        syncWishlist();
      }
      refreshWishlist();
    };

    const initializeWishlist = (swat) => {
      if (!swat) return;
      wishlist.swat = swat;

      if (!wishlist.initialized.has(swat)) {
        wishlist.initialized.add(swat);

        refreshWishlist();

        swat.evtLayer?.addEventListener('sw:addedtowishlist', (event) => updateFromEvent(event, true));
        swat.evtLayer?.addEventListener('sw:removedfromwishlist', (event) => updateFromEvent(event, false));
      }

      syncWishlist();
    };

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(initializeWishlist);
    if (window._swat) initializeWishlist(window._swat);

    class RccProductWishlist extends HTMLElement {
      connectedCallback() {
        wishlist.components.add(this);
        this.render();
      }

      disconnectedCallback() {
        wishlist.components.delete(this);
      }

      set product(value) {
        this._product = value;
        this.render();
      }

      get product() {
        return this._product;
      }

      get ids() {
        return {
          product: shopifyId(this._product?.productId),
          variant: shopifyId(this._product?.variantId),
        };
      }

      render() {
        const { product: productId, variant: variantId } = this.ids;
        const url = productUrl(this._product?.url);

        if (!/^\d+$/.test(productId) || !/^\d+$/.test(variantId) || !url) {
          this.replaceChildren();
          return;
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rcc-product-wishlist__button';
        button.dataset.productId = productId;
        button.dataset.rccWishlistVariantId = variantId;
        button.dataset.productUrl = url;
        button.setAttribute('aria-haspopup', 'dialog');
        button.innerHTML = `
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M19.6706 5.4736C17.6806 3.8336 14.7206 4.1236 12.8906 5.9536L12.0006 6.8436L11.1106 5.9536C9.29063 4.1336 6.32064 3.8336 4.33064 5.4736C2.05064 7.3536 1.93063 10.7436 3.97063 12.7836L11.6406 20.4536C11.8406 20.6536 12.1506 20.6536 12.3506 20.4536L20.0206 12.7836C22.0706 10.7436 21.9506 7.3636 19.6706 5.4736Z" />
          </svg>
        `;
        button.addEventListener('click', (event) => this.toggle(event));
        this.replaceChildren(button);
        this.updateState();
      }

      updateState() {
        const button = this.querySelector('button');
        if (!button) return;

        const added = wishlist.products.has(this.ids.product);
        button.classList.toggle('swym-added', added);
        button.disabled = typeof wishlist.swat?.ui?.performAddToWishlist !== 'function';
        button.setAttribute('aria-pressed', String(added));
        button.setAttribute(
          'aria-label',
          added ? config.wishlistManage || 'Manage wishlists' : config.wishlistAdd || 'Add to wishlist',
        );
      }

      toggle(event) {
        event.preventDefault();
        event.stopPropagation();

        const { product: productId, variant: variantId } = this.ids;
        const url = productUrl(this._product?.url);
        const owner = wishlist.swat?.ui;
        const method = owner?.performAddToWishlist;

        if (typeof method !== 'function' || !/^\d+$/.test(productId) || !/^\d+$/.test(variantId) || !url) {
          return;
        }

        const item = { empi: Number(productId), epi: Number(variantId), du: url };
        const finish = (...response) => {
          const [, removedFromAllLists] = response;
          if (removedFromAllLists) {
            wishlist.variants.delete(variantId);
          } else {
            wishlist.variants.set(variantId, productId);
          }
          syncWishlist();
          refreshWishlist();
        };

        try {
          method.call(owner, item, finish, refreshWishlist);
        } catch (_error) {
          // Leave the current state intact if Swym cannot open its modal.
        }
      }
    }

    customElements.define('rcc-product-wishlist', RccProductWishlist);
  }

  function moneyAmount(money) {
    const amount = Number(money?.amount);
    return Number.isFinite(amount) ? amount : null;
  }

  function badgeTitle(metaobject) {
    return String(
      metaobject?.badgeTitle?.value ||
        metaobject?.fields?.find((field) => field?.key === 'badge_title')?.value ||
        ''
    ).trim();
  }

  function configuredBadgeNodes(product, graphql) {
    const metafield =
      product.metafields?.find(
        (field) => field?.namespace === 'custom' && field?.key === 'product_badges'
      ) || product.metafields?.[0];
    const value = metafield?.value;

    if (graphql.rccProductBadges?.references?.nodes) return graphql.rccProductBadges.references.nodes;
    if (metafield?.references?.nodes) return metafield.references.nodes;
    if (Array.isArray(value)) return value;

    try {
      const parsed = JSON.parse(value || 'null');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  function badgeElement(label, modifier, tagName = 'span') {
    const badge = document.createElement(tagName);
    badge.className = `badge ${modifier}`;
    badge.textContent = label;
    return badge;
  }

  function customBadgeElement(label) {
    const slug = label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    return badgeElement(label, `badge--custom${slug ? ` badge--${slug}` : ''}`);
  }

  function formatSavings(amount) {
    const value = amount
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const moneyFormat = config.moneyFormat || '${{amount}}';
    const formatted = moneyFormat.replace(/\{\{\s*amount\s*\}\}/, value);

    return formatted === moneyFormat ? `$${value}` : formatted;
  }

  if (!customElements.get('rcc-product-badges')) {
    class RccProductBadges extends HTMLElement {
      static get graphqlRequirements() {
        return {
          graphql: [
            `
              rccBadgeAvailable: availableForSale
              rccBadgeCompareAtPriceRange: compareAtPriceRange {
                minVariantPrice { amount currencyCode }
                maxVariantPrice { amount currencyCode }
              }
              rccProductBadges: metafield(namespace: "custom", key: "product_badges") {
                references(first: 20) {
                  nodes {
                    ... on Metaobject {
                      badgeTitle: field(key: "badge_title") { value }
                    }
                  }
                }
              }
            `,
          ],
        };
      }

      connectedCallback() {
        this.render();
      }

      set product(value) {
        this._product = value;
        this.render();
      }

      get product() {
        return this._product;
      }

      render() {
        if (!this._product) {
          this.replaceChildren();
          return;
        }

        const graphql = this._product.graphql || {};
        const tagsValue = this._product.tags || graphql.tags || [];
        const tags = Array.isArray(tagsValue) ? tagsValue.map(String) : String(tagsValue).split(',').map((tag) => tag.trim());
        const variants =
          this._product.variantsAll ||
          this._product.variants ||
          graphql.variants?.nodes ||
          [];
        const selectedId = shopifyId(this._product.variantId);
        const selectedVariant =
          variants.find((variant) => shopifyId(variant.id) === selectedId) ||
          variants.find((variant) => variant.availableForSale) ||
          variants[0];
        const standard = [];
        let custom = configuredBadgeNodes(this._product, graphql).map(badgeTitle).filter(Boolean);

        const exclusive = custom.find((title) => title === 'EVEREVE Exclusive');
        if (exclusive) custom = [exclusive];
        else {
          if (
            config.showNewIn &&
            tags.includes('New!') &&
            !['new-arrivals', 'new-arrivals-newest'].includes(config.collectionHandle)
          ) {
            custom.push('New in!');
          }
          if (config.showNewColor && tags.includes('New Color')) custom.push('New Color');
        }

        if (
          config.showSoldOut &&
          (selectedVariant?.availableForSale === false ||
            (!selectedVariant && graphql.rccBadgeAvailable === false))
        ) {
          standard.push(
            badgeElement(config.soldOutBadge || 'Out of Stock', 'badge--sold-out', 'sold-out-badge')
          );
        }

        if (config.showDiscount && variants.length) {
          const productPrice = moneyAmount(
            this._product.priceRange?.minVariantPrice || graphql.priceRange?.minVariantPrice
          );
          const productCompareAt = moneyAmount(
            graphql.rccBadgeCompareAtPriceRange?.minVariantPrice
          );
          const productCompareAtMax = moneyAmount(
            graphql.rccBadgeCompareAtPriceRange?.maxVariantPrice
          );
          const compareAtVaries =
            productCompareAt != null &&
            productCompareAtMax != null &&
            productCompareAt !== productCompareAtMax;
          const variantPrice = moneyAmount(selectedVariant?.price);
          const variantCompareAt = moneyAmount(selectedVariant?.compareAtPrice);
          const variantOnSale = variantCompareAt != null && variantPrice != null && variantCompareAt > variantPrice;

          if (productCompareAt != null && productPrice != null && productCompareAt > productPrice) {
            let label = config.onSaleBadge || 'On sale';

            if (variantOnSale && !compareAtVaries) {
              const savings =
                config.discountMode === 'percentage'
                  ? `${Math.round(((variantCompareAt - variantPrice) * 100) / variantCompareAt)}%`
                  : formatSavings(variantCompareAt - variantPrice);
              label = String(config.discountBadge || 'Save @@').replace('@@', savings);
            }

            standard.push(badgeElement(label, 'badge--on-sale', 'on-sale-badge'));
          }
        }

        if (config.showBestSeller && tags.includes('Badge: Best Seller')) {
          standard.push(badgeElement('Best Seller', 'badge--best-seller', 'best-seller-badge'));
        }

        const fragment = document.createDocumentFragment();

        if (standard.length) {
          const list = document.createElement('badge-list');
          list.className = 'badge-list badge-list--vertical';
          list.append(...standard);
          fragment.append(list);
        }

        if (custom.length) {
          const list = document.createElement('badge-list');
          list.className = 'badge-list badge-list--custom badge-list--vertical bottom';
          list.append(...custom.map(customBadgeElement));
          fragment.append(list);
        }

        this.replaceChildren(fragment);
      }
    }

    customElements.define('rcc-product-badges', RccProductBadges);
  }
})();
